import { z } from 'zod'
import { isoTimestamp } from './common.js'

/**
 * `users` — REQUIREMENTS.md §5.1. Document ID is the Firebase Auth UID.
 *
 * `cardholderName` and `department` are captured during first-run setup and
 * printed verbatim on the packet log sheet header (§8.1), which is why they're
 * free text rather than derived from the auth profile — the business office
 * expects a specific spelling.
 */
export const userSchema = z.object({
  userId: z.string().min(1),
  email: z.email(),
  displayName: z.string().trim().min(1).max(120),

  /**
   * Personal forward-to address, derived from `ingestToken` and stored so the
   * settings screen and the packet can show it without rebuilding it (§5.1).
   */
  ingestEmailAddress: z.email().nullable().default(null),

  /**
   * The routing secret in that address — §6.1.
   *
   * Unguessable rather than a shared `receipts@` mailbox: anything delivered
   * to the address lands in this user's review queue, so a guessable address
   * lets a stranger fill it with documents that then have to be read and
   * deleted. Never displayed on its own; it only appears inside the address.
   */
  ingestToken: z.string().min(16).max(64).nullable().default(null),

  /**
   * Senders whose forwards are ingested without a confirmation prompt (§6.1).
   * Either a full address or a whole domain written `@example.com`. Mail from
   * anywhere else is still ingested — it is flagged, not dropped.
   */
  trustedSenders: z.array(z.string().trim().min(3).max(160)).max(100).default([]),

  /** As it must appear on the packet log sheet. */
  cardholderName: z.string().trim().max(120).default(''),
  /** Department/Building line on the log sheet header. */
  department: z.string().trim().max(160).default(''),

  /** Above this dollar amount, "no receipt" prompts a confirmation (§7.2). */
  noReceiptWarningThreshold: z.number().min(0).default(10),

  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
})

export type User = z.infer<typeof userSchema>

/** First-run setup form — profile half (workflow: step 4 setup wizard). */
export const userProfileSetupSchema = z.object({
  cardholderName: z.string().trim().min(1, 'Required — appears on every packet.').max(120),
  department: z.string().trim().min(1, 'Required — appears on every packet.').max(160),
})
export type UserProfileSetup = z.infer<typeof userProfileSetupSchema>
